import { Suspense, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, Slider, Typography } from "@mui/material";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash-es";

import { ButtonLoading } from "src/components/ButtonLoading";
import { ResidentialZoneRestrictionsTable } from "./ResidentialZoneRestrictionsTable";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { IsDisabled, IsEnabled } from "src/components/permissions";
import { useScenario } from "src/hooks/useScenario";
import {
  RestrictionsMutationData,
  useResidentialZone,
  useResidentialZoneRestrictionsUpdateMutation,
} from "src/hooks/residential-zones";
import { isDisabled } from "src/utils/permission.utils";
import { ValueLabelComponent } from "./ResctrictionRow";
import { Spacer } from "./Spacer";

export function ResidentialZoneRestrictionsDialog(props: {
  scenarioId: number | string;
  residentialZoneId: number | string;
  open: boolean;
  onClose: () => void;
}) {
  const { data: scenario } = useScenario(props.scenarioId);
  const { data: residentialZone } = useResidentialZone(props.scenarioId, props.residentialZoneId);

  const { t } = useTranslation();

  // dirty data is non-persisted fleet config
  const [weightList, setWeightList] = useState<RestrictionsMutationData["weightList"] | null>(null);
  const [totalWeight, setTotalWeight] = useState<number | null>(null);

  const updateMutation = useResidentialZoneRestrictionsUpdateMutation(props.scenarioId, props.residentialZoneId);

  /**
   * Effects
   */
  // set dirty data to initial fleetconfig payload
  useEffect(() => {
    if (!residentialZone) return;
    else setTotalWeight(residentialZone.totalWeight);
  }, [residentialZone]);

  /**
   * Functions
   */

  function handleChange(config: RestrictionsMutationData["weightList"]) {
    setWeightList(config);
  }

  function handleTotalWeightChange(totalWeight: number) {
    setTotalWeight(totalWeight);
  }

  function handleSave() {
    if (!weightList) return;
    if (!totalWeight) return;

    const dataToSubmit: RestrictionsMutationData = { weightList, totalWeight };
    updateMutation.mutateAsync(dataToSubmit).then(() => props.onClose());
  }

  // render the component
  return render();

  function render() {
    if (!scenario) return null;

    return (
      <StyledDialog fullWidth maxWidth="md" open={props.open}>
        <DialogContent>
          <Typography variant="h2" gutterBottom>
            {t("label.residentialZones")}
          </Typography>

          {renderEnergyEfficiencyMeasure()}

          <Typography variant="h3" gutterBottom>
            Specific measures
            {/* {t("label.residentialZones")} */}
          </Typography>

          <Suspense fallback={<FleetConfigLoading />}>
            <ResidentialZoneRestrictionsTable
              className="table"
              scenarioId={props.scenarioId}
              residentialZoneId={props.residentialZoneId}
              readOnly={isDisabled(scenario.permissions.updateResidential)}
              onChange={handleChange}
            />
          </Suspense>
        </DialogContent>

        <DialogActions>
          {updateMutation.error?.response?.data && <ServerErrors data={updateMutation.error.response.data} />}

          <IsDisabled permission={scenario.permissions.updateResidential}>
            <Button onClick={() => props.onClose()} color="primary">
              {t("label.close")}
            </Button>
          </IsDisabled>

          <IsEnabled permission={scenario.permissions.updateResidential}>
            <Button onClick={() => props.onClose()} color="primary">
              {t("label.cancel")}
            </Button>

            <ButtonLoading disabled={weightList === null} isLoading={updateMutation.isLoading} onClick={handleSave}>
              {t("label.save")}
            </ButtonLoading>
          </IsEnabled>
        </DialogActions>
      </StyledDialog>
    );
  }

  function renderEnergyEfficiencyMeasure() {
    if (totalWeight === null) return null;

    return (
      <div className="energy-efficiency-measure">
        <Typography gutterBottom>{t("label.energyEfficiencyMeasure")}</Typography>

        <Spacer />

        <Slider
          size="small"
          min={0}
          max={200}
          value={totalWeight}
          valueLabelDisplay="auto"
          components={{
            ValueLabel: ValueLabelComponent,
          }}
          onChange={debounce((event, newValue) => {
            if (!Array.isArray(newValue)) handleTotalWeightChange(newValue);
          })}
        />
      </div>
    );
  }
}

const StyledDialog = styled(Dialog)`
  --slider-width: 170px;

  .MuiDialogActions-root {
    padding: ${({ theme }) => theme.spacing(3)};
  }

  .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
    overflow: auto;
    height: 100vh;
    max-height: 900px;
  }
  .table {
    height: 0;
    flex: 1 1 100px;
  }

  .energy-efficiency-measure {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: ${({ theme }) => theme.spacing(2)};
    margin-bottom: ${({ theme }) => theme.spacing(2)};
    background-color: #f7f7f7;
    border: 1px solid #e0e0e0;

    .MuiTypography-root,
    .MuiSlider-root {
      flex-shrink: 0;
    }

    .MuiSlider-root {
      width: var(--slider-width);
      margin-right: ${({ theme }) => theme.spacing(1)};
    }
  }
`;

const FleetConfigLoading = styled.div`
  height: 100vh;
`;
